import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import MenuIcon from '@mui/icons-material/Menu'
import RefreshIcon from '@mui/icons-material/Refresh'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BusinessIcon from '@mui/icons-material/BusinessOutlined'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import CheckIcon from '@mui/icons-material/Check'

import { alpha, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useAuth } from '../auth/AuthContext'
import { tokens } from '../theme'
import { useThemeMode } from '../theme/ThemeModeContext'
import UnreadBadge from './UnreadBadge'

// Route → page-meta map. Kept exhaustive so the top bar never shows a
// stale title. If a route is missing, the fallback below reads the last
// path segment prettified.
const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/':                    { title: 'Control Tower',           subtitle: 'Exception queue · live rank of jobs needing attention now' },
  '/readiness':           { title: 'Pre-flight Readiness',    subtitle: 'What can still be fixed before the van rolls' },
  '/map':                 { title: 'Live Map',                subtitle: 'Engineer positions · postcode heat · open exceptions' },
  '/analytics':           { title: 'Exception Analytics',     subtitle: 'FTF trend · category mix · patch hotspots' },
  '/asset-health':        { title: 'Network & Asset Health',  subtitle: 'Roll-up faults by exchange · cabinet · DP · asset' },
  '/cost':                { title: 'Cost-to-Serve',           subtitle: '£/visit by patch × workforce tier × job type' },
  '/notifications-prefs': { title: 'Notification preferences',subtitle: 'Digest cadence · critical alerts · in-app toasts' },
  '/admin/users':         { title: 'Users',                   subtitle: 'Tenant users · roles · password reset' },
  '/admin/sources':       { title: 'Data sources',            subtitle: 'WFM adapter config · HMAC secret rotation' },
  '/admin/tenant':        { title: 'Tenant profile',         subtitle: 'This tenant’s display settings' },
  '/admin/audit':         { title: 'Audit trail',             subtitle: 'Append-only record of every admin action' },
  '/platform/tenants':    { title: 'Tenants',                 subtitle: 'Every FieldLens workspace on this instance' },
  '/platform/sessions':   { title: 'Sessions',                subtitle: 'Every token issued in the selected window' },
  '/platform':            { title: 'Platform Admin',          subtitle: 'Cross-tenant administration · Insight Delivered' },
}

function membershipKey(m: { tenant_id: string; role: string }): string {
  return m.tenant_id + '::' + m.role
}

function metaFor(pathname: string) {
  const exact = PAGE_META[pathname]
  if (exact) return exact
  // Prefix match for admin index redirect etc.
  for (const key of Object.keys(PAGE_META)) {
    if (key !== '/' && pathname.startsWith(key)) return PAGE_META[key]
  }
  const seg = pathname.split('/').filter(Boolean).pop() ?? ''
  return { title: seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'FieldLens', subtitle: '' }
}

type Props = {
  wsStatus: 'idle' | 'connecting' | 'open' | 'closed'
  onMobileNavToggle: () => void
}

export default function TopBar({ wsStatus, onMobileNavToggle }: Props) {
  const { user, logout, switchMembership, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const themeMode = useThemeMode()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [switching, setSwitching] = useState<string | null>(null)
  const meta = useMemo(() => metaFor(location.pathname), [location.pathname])

  const memberships = user?.memberships ?? []
  const activeKey = user?.active ? membershipKey(user.active) : ''
  const canSwitch = memberships.length > 1

  async function handleSwitch(m: (typeof memberships)[number]) {
    if (loading || switching) return
    const key = membershipKey(m)
    if (key === activeKey) {
      setAnchor(null)
      return
    }
    setSwitching(key)
    try {
      await switchMembership(m.tenant_id, m.role)
      setAnchor(null)
      // Invalidate every mounted tenant-scoped query so the new context
      // pulls fresh data. TanStack Query only refetches what's mounted.
      void qc.invalidateQueries()
      // Route to the natural landing page for the new membership.
      navigate(m.is_platform ? '/platform/tenants' : '/', { replace: true })
    } catch {
      // AuthContext.error carries the ApiError — TopBar just reverts.
    } finally {
      setSwitching(null)
    }
  }

  const initials = (user?.display_name ?? user?.email ?? 'FL')
    .split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()

  const live = wsStatus === 'open'
  const chipColor = live ? tokens.success : wsStatus === 'connecting' ? tokens.warning : tokens.alert
  const chipLabel = live ? 'Live data' : wsStatus === 'connecting' ? 'Connecting…' : 'Offline'

  const refresh = () => {
    // Nuclear invalidate — every query key that pages use. Cheap because
    // TanStack Query only refetches the mounted queries.
    void qc.invalidateQueries()
  }

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 3 },
        py: 1.25,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 1,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) => alpha(t.palette.background.paper, t.palette.mode === 'dark' ? 0.6 : 0.85),
        backdropFilter: 'blur(8px)',
        position: 'sticky', top: 0, zIndex: 10,
      }}
    >
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {isMobile && (
          <Tooltip title="Open menu">
            <IconButton size="small" onClick={onMobileNavToggle} aria-label="Open navigation">
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meta.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', display: { xs: 'none', sm: 'block' } }}>
            {meta.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Right controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Tooltip title={live ? 'WebSocket stream is live — invalidations are pushed' : `Stream ${wsStatus}`}>
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '12px !important', color: `${chipColor} !important` }} />}
            label={chipLabel}
            size="small"
            sx={{
              height: 22,
              '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 600, color: chipColor },
              bgcolor: alpha(chipColor, 0.10),
              border: `1px solid ${alpha(chipColor, 0.30)}`,
            }}
          />
        </Tooltip>

        {user?.tenant_slug && (
          <Chip
            label={user.tenant_slug}
            size="small"
            sx={{
              height: 22, textTransform: 'uppercase', letterSpacing: '0.06em',
              '& .MuiChip-label': { px: 1, fontSize: '0.65rem', fontWeight: 700 },
              bgcolor: alpha(tokens.indigo, 0.10),
              border: `1px solid ${alpha(tokens.indigo, 0.25)}`,
              color: tokens.indigo,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          />
        )}

        <Tooltip title="Refresh data">
          <IconButton size="small" onClick={refresh} aria-label="Refresh">
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={themeMode.mode === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}>
          <IconButton size="small" onClick={themeMode.toggle} aria-label="Toggle theme">
            {themeMode.mode === 'light'
              ? <DarkModeIcon sx={{ fontSize: 18 }} />
              : <LightModeIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Notification preferences">
          <IconButton size="small" onClick={() => navigate('/notifications-prefs')} aria-label="Notification preferences">
            <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <UnreadBadge />

        {/* Avatar / user menu */}
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAnchor(e.currentTarget as HTMLElement) }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
            ml: 0.5, px: 1, py: 0.5, borderRadius: 2,
            border: `1px solid ${alpha(tokens.gold, 0.25)}`,
            bgcolor: alpha(tokens.gold, 0.07),
            '&:hover': { bgcolor: alpha(tokens.gold, 0.12) },
            '&:focus-visible': { outline: `2px solid ${tokens.gold}`, outlineOffset: 1 },
          }}
        >
          <Box sx={{
            width: 22, height: 22, borderRadius: 1,
            bgcolor: alpha(tokens.gold, 0.2),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: tokens.gold }}>{initials}</Typography>
          </Box>
          <Typography sx={{
            fontWeight: 600, fontSize: '0.72rem', maxWidth: 120,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}>
            {user?.display_name?.split(' ')[0] ?? user?.email?.split('@')[0] ?? 'You'}
          </Typography>
        </Box>

        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 260 } } }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{user?.display_name ?? user?.email}</Typography>
            <Typography sx={{ color: tokens.gold, textTransform: 'capitalize', fontSize: '0.70rem' }}>
              {user?.roles?.[0]?.replace(/_/g, ' ') ?? 'user'}
            </Typography>
            {user?.tenant_slug && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>
                {user.tenant_slug}
              </Typography>
            )}
          </Box>

          {/* Workspace switcher — visible whenever the user has more than one
              membership. A single-workspace user never sees it. */}
          {canSwitch && [
            <Divider key="d1" />,
            <Box key="wshdr" sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.14em', fontSize: '0.62rem', fontWeight: 700 }}>
                Switch workspace
              </Typography>
            </Box>,
            ...memberships.map((m) => {
              const key = membershipKey(m)
              const isActive = key === activeKey
              const isSwitchingThis = switching === key
              return (
                <MenuItem
                  key={key}
                  onClick={() => handleSwitch(m)}
                  disabled={!!switching}
                  sx={{ fontSize: '0.82rem', gap: 1 }}
                >
                  <ListItemIcon>
                    {m.is_platform
                      ? <AdminIcon sx={{ fontSize: 18, color: tokens.gold }} />
                      : <BusinessIcon sx={{ fontSize: 18, color: tokens.indigo }} />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={m.is_platform ? 'Platform' : (m.tenant_name || m.tenant_slug)}
                    secondary={m.role.replace(/_/g, ' ')}
                    slotProps={{
                      primary:   { sx: { fontSize: '0.82rem', fontWeight: isActive ? 700 : 500 } },
                      secondary: { sx: { fontSize: '0.66rem', textTransform: 'capitalize', color: 'text.secondary' } },
                    }}
                  />
                  {isActive && !isSwitchingThis && <CheckIcon sx={{ fontSize: 16, color: tokens.success }} />}
                  {isSwitchingThis && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Switching…</Typography>
                  )}
                </MenuItem>
              )
            }),
          ]}

          <Divider />
          <MenuItem
            onClick={() => { navigate('/notifications-prefs'); setAnchor(null) }}
            sx={{ fontSize: '0.82rem', gap: 1 }}
          >
            <ListItemIcon><SettingsOutlinedIcon sx={{ fontSize: 16 }} /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.82rem' } } }}>Notification preferences</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => { setAnchor(null); logout() }}
            sx={{ fontSize: '0.82rem', gap: 1, color: tokens.alert }}
          >
            <ListItemIcon><LogoutIcon sx={{ fontSize: 16, color: tokens.alert }} /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.82rem' } } }}>Sign out</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )
}
